// src/components/packages/PackageForm.tsx

import React from 'react';
import { useForm } from 'react-hook-form';
import { CreatePackageCommand } from '../../types/package.types';

interface Props {
  onSubmit: (data: CreatePackageCommand) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  error: string | null;
}

interface FieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, error, children }) => (
  <div className={`pkg-field ${error ? 'pkg-field--error' : ''}`}>
    <label className="pkg-field__label">{label}</label>
    {children}
    {error && <p className="pkg-field__error">{error}</p>}
  </div>
);

export const PackageForm: React.FC<Props> = ({ onSubmit, onCancel, isLoading, error }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<CreatePackageCommand>();

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="pkg-form">

      {error && (
        <div className="lgt-alert lgt-alert--error" role="alert">
          <span className="lgt-alert__icon">⚠</span>
          <span className="lgt-alert__text">{error}</span>
        </div>
      )}

      {/* ── Recipient ── */}
      <section className="pkg-form__section">
        <h3 className="pkg-form__section-title">Destinatario</h3>
        <div className="pkg-form__grid pkg-form__grid--3">
          <Field label="Nombre completo" error={errors.recipientName?.message}>
            <input className="pkg-input" placeholder="Juan Pérez"
              {...register('recipientName', { required: 'El nombre es requerido', maxLength: { value: 100, message: 'Máximo 100 caracteres' } })} />
          </Field>
          <Field label="Correo electrónico" error={errors.recipientEmail?.message}>
            <input className="pkg-input" type="email" placeholder="juan@correo.com"
              {...register('recipientEmail', {
                required: 'El correo es requerido',
                pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Correo inválido' }
              })} />
          </Field>
          <Field label="Teléfono" error={errors.recipientPhone?.message}>
            <input className="pkg-input" placeholder="+57300000000"
              {...register('recipientPhone', {
                required: 'El teléfono es requerido',
                pattern: { value: /^\+?[1-9]\d{1,14}$/, message: 'Teléfono inválido' }
              })} />
          </Field>
        </div>
      </section>

      {/* ── Address ── */}
      <section className="pkg-form__section">
        <h3 className="pkg-form__section-title">Dirección de entrega</h3>
        <div className="pkg-form__grid pkg-form__grid--2">
          <Field label="Calle / Dirección" error={errors.street?.message}>
            <input className="pkg-input" placeholder="Calle 80 # 45-12"
              {...register('street', { required: 'La dirección es requerida' })} />
          </Field>
          <Field label="Ciudad" error={errors.city?.message}>
            <input className="pkg-input" placeholder="Medellín"
              {...register('city', { required: 'La ciudad es requerida' })} />
          </Field>
          <Field label="Departamento / Estado">
            <input className="pkg-input" placeholder="Antioquia"
              {...register('state')} />
          </Field>
          <Field label="País" error={errors.country?.message}>
            <input className="pkg-input" placeholder="Colombia"
              {...register('country', { required: 'El país es requerido' })} />
          </Field>
          <Field label="Código postal" error={errors.postalCode?.message}>
            <input className="pkg-input" placeholder="050010"
              {...register('postalCode', {
                required: 'El código postal es requerido',
                minLength: { value: 3, message: 'Mínimo 3 caracteres' },
                maxLength: { value: 10, message: 'Máximo 10 caracteres' },
              })} />
          </Field>
        </div>
      </section>

      {/* ── Dimensions ── */}
      <section className="pkg-form__section">
        <h3 className="pkg-form__section-title">Dimensiones y peso</h3>
        <div className="pkg-form__grid pkg-form__grid--4">
          <Field label="Alto (cm)" error={errors.height?.message}>
            <input className="pkg-input" type="number" step="0.1" placeholder="30"
              {...register('height', {
                required: 'Requerido', valueAsNumber: true,
                min: { value: 0.1, message: 'Debe ser mayor a 0' },
                max: { value: 500, message: 'Máx 500 cm' },
              })} />
          </Field>
          <Field label="Ancho (cm)" error={errors.width?.message}>
            <input className="pkg-input" type="number" step="0.1" placeholder="20"
              {...register('width', {
                required: 'Requerido', valueAsNumber: true,
                min: { value: 0.1, message: 'Debe ser mayor a 0' },
                max: { value: 500, message: 'Máx 500 cm' },
              })} />
          </Field>
          <Field label="Profundidad (cm)" error={errors.depth?.message}>
            <input className="pkg-input" type="number" step="0.1" placeholder="15"
              {...register('depth', {
                required: 'Requerido', valueAsNumber: true,
                min: { value: 0.1, message: 'Debe ser mayor a 0' },
                max: { value: 500, message: 'Máx 500 cm' },
              })} />
          </Field>
          <Field label="Peso (kg)" error={errors.weight?.message}>
            <input className="pkg-input" type="number" step="0.01" placeholder="2.5"
              {...register('weight', {
                required: 'Requerido', valueAsNumber: true,
                min: { value: 0.01, message: 'Debe ser mayor a 0' },
                max: { value: 1000, message: 'Máx 1000 kg' },
              })} />
          </Field>
        </div>
      </section>

      {/* ── Notes ── */}
      <section className="pkg-form__section">
        <Field label="Notas adicionales">
          <textarea className="pkg-input pkg-input--textarea" rows={3}
            placeholder="Instrucciones especiales de entrega..."
            {...register('notes', { maxLength: { value: 500, message: 'Máximo 500 caracteres' } })} />
        </Field>
      </section>

      {/* ── Actions ── */}
      <div className="pkg-form__actions">
        <button type="button" className="lgt-btn lgt-btn--ghost" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </button>
        <button type="submit" className="lgt-btn lgt-btn--primary" disabled={isLoading} aria-busy={isLoading}>
          {isLoading ? (
            <><span className="lgt-btn__spinner" aria-hidden="true" /> Creando...</>
          ) : 'Crear paquete'}
        </button>
      </div>
    </form>
  );
};
